/**
 * A project's documents: what is in it, how far each has got, and the upload.
 *
 * Inline under its project in the rail, like sharing and instructions — the
 * project is where its files belong.
 *
 * Four decisions worth stating.
 *
 * Every status is said in plain words, and the server's own detail is shown
 * beside it whenever there is one. "Page 2 has no text layer and was not read"
 * is the sentence somebody needs before they trust an answer built on page 2,
 * and only the server knows it.
 *
 * Downloading and reading the text are offered from the server's own flags
 * (`downloadable`) and status, never re-derived here: a file in quarantine is
 * not offered, because the server would refuse it anyway.
 *
 * The list refreshes itself while anything is still being checked or read,
 * and stops — when nothing is left, when the panel closes, or after a couple
 * of minutes, at which point a Refresh button takes over. A scanner that has
 * gone away should not mean a tab polling forever.
 *
 * Uploading and deleting are offered to the owner and to editors. The role is
 * read from the project's members; when that cannot be read the controls are
 * shown and the server's refusal is what somebody sees.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Download, FileText, RefreshCw, Trash2, Upload, X } from 'lucide-react';
import type {
  AiFileStatus,
  AiProjectFileView,
  AiProjectMemberView,
  AiProjectUsageView,
  AiProjectView,
} from '@cenox/shared';
import { ApiError, api, downloadFile, uploadWithProgress } from '../../../lib/api';
import { useAuth, useBusinessPath } from '../../../lib/auth';
import { Spinner, cn } from '../../../components/ui';
import { FileTextDrawer, type FileTextTarget } from './FileTextDrawer';

/** What the server holds document text to, per question, when /usage cannot say. */
const DEFAULT_RETRIEVAL_BUDGET = 3_000;

/** How the list refreshes while files are being checked or read. */
const POLL_MS = 3_000;
const POLL_CAP_MS = 2 * 60_000;

const STILL_WORKING: ReadonlySet<AiFileStatus> = new Set(['pending_scan', 'pending']);

/** The words for each status, and how loudly to say them. */
const STATUS: Record<AiFileStatus, { label: string; tone: 'neutral' | 'busy' | 'good' | 'warn' | 'bad' }> = {
  pending_scan: { label: 'Checking for malware', tone: 'busy' },
  infected: { label: 'Blocked: malware found', tone: 'bad' },
  pending: { label: 'Reading…', tone: 'busy' },
  extracted: { label: 'Ready', tone: 'good' },
  needs_ocr: { label: 'Scan — text not readable yet', tone: 'warn' },
  unsupported: { label: 'Can’t be read', tone: 'warn' },
  failed: { label: 'Couldn’t be read', tone: 'bad' },
};

const TONE = {
  neutral: 'bg-slate-100 text-slate-700',
  busy: 'bg-brand-50 text-brand-700',
  good: 'bg-emerald-50 text-emerald-700',
  warn: 'bg-amber-50 text-amber-800',
  bad: 'bg-red-50 text-red-700',
} as const;

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** One file waiting for, or in the middle of, its upload. */
interface Queued {
  key: number;
  file: File;
  progress: number;
  state: 'waiting' | 'sending' | 'failed';
  error: string | null;
}

export function ProjectFiles({
  project,
  pollMs = POLL_MS,
  pollCapMs = POLL_CAP_MS,
}: {
  project: AiProjectView;
  /** Shortened by tests; nothing in the app changes them. */
  pollMs?: number;
  pollCapMs?: number;
}): ReactNode {
  const { user } = useAuth();
  const path = useBusinessPath();
  const client = useQueryClient();
  const base = `/ai/projects/${project.id}`;

  const [queue, setQueue] = useState<Queued[]>([]);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reading, setReading] = useState<FileTextTarget | null>(null);
  /** When the current spell of refreshing began, and whether it has run out. */
  const [pollSince, setPollSince] = useState(() => Date.now());
  const [capped, setCapped] = useState(false);
  const picker = useRef<HTMLInputElement | null>(null);
  const nextKey = useRef(0);
  /** The files not yet started, in order. A ref, because the loop outlives renders. */
  const waiting = useRef<Queued[]>([]);
  const running = useRef(false);

  const members = useQuery({
    queryKey: ['ai-project-members', project.id],
    queryFn: () => api.get<{ data: AiProjectMemberView[] }>(path(`${base}/members`)),
    enabled: !project.mine,
  });
  const myRole = members.data?.data.find((m) => m.userId === user?.id)?.role;
  const canEdit = project.mine || myRole === 'owner' || myRole === 'editor' || members.isError;

  const isWorking = (list: AiProjectFileView[] | undefined): boolean =>
    (list ?? []).some((f) => STILL_WORKING.has(f.status));

  const files = useQuery({
    queryKey: ['ai-project-files', project.id],
    queryFn: () => api.get<{ data: AiProjectFileView[]; uploadsAvailable?: boolean }>(path(`${base}/files`)),
    // A query option, so it stops by itself when the panel closes.
    refetchInterval: (query) => (!capped && isWorking(query.state.data?.data) ? pollMs : false),
  });
  const rows = files.data?.data ?? [];
  const working = isWorking(rows);

  useEffect(() => {
    if (!working || capped) return;
    const timer = setTimeout(() => setCapped(true), Math.max(0, pollCapMs - (Date.now() - pollSince)));
    return () => clearTimeout(timer);
  }, [working, capped, pollSince, pollCapMs]);

  const restartPolling = (): void => {
    setPollSince(Date.now());
    setCapped(false);
  };

  const usage = useQuery({
    queryKey: ['ai-project-usage', project.id],
    queryFn: () => api.get<AiProjectUsageView>(path(`${base}/usage`)),
    staleTime: 5 * 60_000,
  });
  const budget = usage.data?.retrievalBudget ?? DEFAULT_RETRIEVAL_BUDGET;

  const refreshFiles = (): Promise<void> =>
    client.invalidateQueries({ queryKey: ['ai-project-files', project.id] });

  /*
    One upload at a time, in the order chosen. Several at once would share the
    connection and every bar would crawl; one after another, each finishes.
  */
  async function drain(): Promise<void> {
    if (running.current) return;
    running.current = true;
    try {
      for (let item = waiting.current.shift(); item; item = waiting.current.shift()) {
        const key = item.key;
        setQueue((q) => q.map((x) => (x.key === key ? { ...x, state: 'sending' } : x)));
        const form = new FormData();
        form.append('file', item.file);
        try {
          await uploadWithProgress<AiProjectFileView>(path(`${base}/files`), form, (fraction) =>
            setQueue((q) => q.map((x) => (x.key === key ? { ...x, progress: fraction } : x))),
          );
          setQueue((q) => q.filter((x) => x.key !== key));
          restartPolling();
          void refreshFiles();
        } catch (caught) {
          const message = caught instanceof ApiError ? caught.message : 'That file could not be uploaded.';
          setQueue((q) =>
            q.map((x) => (x.key === key ? { ...x, state: 'failed', error: message } : x)),
          );
        }
      }
    } finally {
      running.current = false;
    }
  }

  const choose = (chosen: FileList | null): void => {
    if (!chosen || chosen.length === 0) return;
    const added = [...chosen].map<Queued>((file) => ({
      key: nextKey.current++,
      file,
      progress: 0,
      state: 'waiting',
      error: null,
    }));
    setQueue((q) => [...q, ...added]);
    waiting.current.push(...added);
    void drain();
  };

  const remove = useMutation({
    mutationFn: (fileId: string) => api.del(path(`${base}/files/${fileId}`)),
    onSuccess: () => {
      setConfirmingId(null);
      setActionError(null);
      void refreshFiles();
      void client.invalidateQueries({ queryKey: ['ai-project-usage', project.id] });
    },
    onError: (caught) => {
      setConfirmingId(null);
      setActionError(caught instanceof ApiError ? caught.message : 'Could not delete that file.');
    },
  });

  const download = async (f: AiProjectFileView): Promise<void> => {
    setActionError(null);
    try {
      await downloadFile(path(`${base}/files/${f.id}/content`), f.title);
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught.message : 'Could not download that file.');
    }
  };

  return (
    <section aria-label={`Files in ${project.name}`} className="px-2 pb-3">
      {/*
        Said once, near the list, because it decides what a file is worth
        adding: a document is searched by its words, and only so much of it
        travels with any one question.
      */}
      <p className="text-[11px] leading-snug text-slate-500">
        CenoX AI searches the words in these documents (not their meaning), using up to ~
        {budget.toLocaleString('en-US')} tokens of document text per question.
      </p>

      {canEdit && files.data?.uploadsAvailable !== false && (
        <div className="mt-2">
          <input
            ref={picker}
            type="file"
            multiple
            className="sr-only"
            aria-label={`Upload files to ${project.name}`}
            onChange={(e) => {
              choose(e.target.files);
              // So choosing the same file again, after a failure, still fires.
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={() => picker.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            <Upload className="size-3.5" aria-hidden />
            Upload files
          </button>
        </div>
      )}

      {queue.length > 0 && (
        <ul aria-label="Uploads" className="mt-2 space-y-1.5">
          {queue.map((item) => (
            <li key={item.key} className="text-xs">
              <div className="flex items-center gap-1">
                <span className="min-w-0 flex-1 truncate text-slate-700">{item.file.name}</span>
                {item.state === 'failed' && (
                  <button
                    type="button"
                    aria-label={`Dismiss ${item.file.name}`}
                    onClick={() => setQueue((q) => q.filter((x) => x.key !== item.key))}
                    className="rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  >
                    <X className="size-3.5" aria-hidden />
                  </button>
                )}
              </div>
              {item.state === 'failed' ? (
                <p role="alert" className="mt-0.5 text-[11px] text-red-700">
                  {item.error}
                </p>
              ) : (
                <div
                  role="progressbar"
                  aria-label={`Uploading ${item.file.name}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(item.progress * 100)}
                  className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100"
                >
                  <div
                    className="h-full rounded-full bg-brand-500 transition-[width]"
                    style={{ width: `${Math.round(item.progress * 100)}%` }}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {actionError && (
        <p role="alert" className="mt-2 text-[11px] text-red-700">
          {actionError}
        </p>
      )}

      {files.isPending ? (
        <div className="pt-2">
          <Spinner label="Loading files" />
        </div>
      ) : files.isError ? (
        <p role="alert" className="mt-2 text-[11px] text-red-700">
          {files.error instanceof ApiError ? files.error.message : 'Could not load the files.'}
        </p>
      ) : rows.length === 0 ? (
        <p className="mt-2 text-xs text-slate-500">No files yet.</p>
      ) : (
        <ul className="mt-2 space-y-2">
          {rows.map((f) => (
            <FileRow
              key={f.id}
              file={f}
              canEdit={canEdit}
              confirming={confirmingId === f.id}
              deleting={remove.isPending && remove.variables === f.id}
              onDownload={() => void download(f)}
              onRead={() => setReading({ projectId: project.id, fileId: f.id, title: f.title, offset: 0 })}
              onAskDelete={() => setConfirmingId(f.id)}
              onCancelDelete={() => setConfirmingId(null)}
              onDelete={() => remove.mutate(f.id)}
            />
          ))}
        </ul>
      )}

      {working && capped && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500">
          <span>Still working on some files.</span>
          <button
            type="button"
            onClick={() => {
              restartPolling();
              void files.refetch();
            }}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-brand-700 hover:bg-brand-50"
          >
            <RefreshCw className="size-3" aria-hidden />
            Refresh
          </button>
        </div>
      )}

      {reading && <FileTextDrawer target={reading} onClose={() => setReading(null)} />}
    </section>
  );
}

function FileRow({
  file: f,
  canEdit,
  confirming,
  deleting,
  onDownload,
  onRead,
  onAskDelete,
  onCancelDelete,
  onDelete,
}: {
  file: AiProjectFileView;
  canEdit: boolean;
  confirming: boolean;
  deleting: boolean;
  onDownload: () => void;
  onRead: () => void;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;
}): ReactNode {
  // A status nobody planned for is said as such, not as a pass.
  const status = STATUS[f.status] ?? { label: f.status, tone: 'neutral' as const };
  const pages = f.status === 'extracted' && f.pageCount ? ` · ${f.pageCount} ${f.pageCount === 1 ? 'page' : 'pages'}` : '';

  return (
    <li className="rounded-lg border border-slate-200 px-2 py-1.5">
      <div className="flex items-start gap-1">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-slate-800" title={f.title}>
            {f.title}
          </p>
          <p className="truncate text-[10px] text-slate-400">
            {formatBytes(f.byteSize)}
            {f.uploaderName && !f.mine ? ` · ${f.uploaderName}` : ''}
          </p>
        </div>
        {!confirming && (
          <span className="flex shrink-0 items-center">
            {f.downloadable && (
              <IconButton label={`Download ${f.title}`} onClick={onDownload}>
                <Download className="size-3.5" aria-hidden />
              </IconButton>
            )}
            {f.status === 'extracted' && (
              <IconButton label={`View text of ${f.title}`} onClick={onRead}>
                <FileText className="size-3.5" aria-hidden />
              </IconButton>
            )}
            {canEdit && (
              <IconButton label={`Delete ${f.title}`} onClick={onAskDelete}>
                <Trash2 className="size-3.5" aria-hidden />
              </IconButton>
            )}
          </span>
        )}
        {/* The second ask, inline, as everywhere else in the rail. */}
        {confirming && (
          <span className="flex shrink-0 items-center gap-1">
            <span className="text-[11px] text-slate-500">Delete?</span>
            <IconButton label={`Confirm delete ${f.title}`} onClick={onDelete} disabled={deleting}>
              <Check className="size-3.5 text-red-600" aria-hidden />
            </IconButton>
            <IconButton label="Cancel delete" onClick={onCancelDelete}>
              <X className="size-3.5" aria-hidden />
            </IconButton>
          </span>
        )}
      </div>
      <p className="mt-1">
        <span
          data-status={f.status}
          className={cn('inline-block rounded-full px-1.5 py-0.5 text-[10px] font-medium', TONE[status.tone])}
        >
          {status.label}
          {pages}
        </span>
      </p>
      {f.statusDetail && (
        <p className={cn('mt-0.5 text-[11px] leading-snug', status.tone === 'bad' ? 'text-red-700' : 'text-slate-500')}>
          {f.statusDetail}
        </p>
      )}
    </li>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
    >
      {children}
    </button>
  );
}
